import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";

import MobileImageViewer from "mobile/components/MobileImageViewer";
import MobilePage from "mobile/components/MobilePage";
import { api } from "lib/api";

const getFileUrl = (file) => {
  if (!file) return null;
  const urlPath = file?.url || file?.attributes?.url;
  if (!urlPath) return null;
  const base = api?.defaults?.baseURL || "";
  if (/^https?:\/\//i.test(urlPath)) return urlPath;
  return `${base}${urlPath}`;
};

const primaryOwner = (file) => file?.primaryOwner || file?.owners?.[0] || null;

const ownershipTitle = (file) => {
  const owner = primaryOwner(file);
  if (owner?.label) return owner.label;
  return "Unattached";
};

const ownershipSubtitle = (file) => {
  const owner = primaryOwner(file);
  if (owner?.detail) return owner.detail;
  if (owner?.kind === "valve-body") return "Valve body";
  if (owner?.kind === "build-sheet") return "Build sheet";
  return file?.mime || "Library file";
};

const OWNER_FILTERS = [
  { value: "all", label: "All" },
  { value: "valve-body", label: "Valve bodies" },
  { value: "build-sheet", label: "Build sheets" },
  { value: "unattached", label: "Unattached" },
];

export default function ImageGalleryMobile() {
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const PAGE_SIZE = 30;
  const loadMoreRef = useRef(null);

  const [selectedFolderId, setSelectedFolderId] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | images | pdfs | other
  const [ownerFilter, setOwnerFilter] = useState("all"); // all | valve-body | build-sheet | unattached
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [uploadDialog, setUploadDialog] = useState({ open: false, folderId: 1 });
  const [uploadStatus, setUploadStatus] = useState({ uploading: false, stage: "idle", total: 0, moved: 0, uploaded: 0, error: "" });
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [renameFolderName, setRenameFolderName] = useState("");
  const [moveDialog, setMoveDialog] = useState({ open: false, file: null, targetFolderId: 1 });
  const [viewer, setViewer] = useState({ open: false, file: null });
  const [renameFile, setRenameFile] = useState({ open: false, id: null, name: "" });

  // Support deep-linking from AI/search results: /media/gallery?folderId=1&q=foo&type=images&ownerKind=valve-body
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const folderId = params.get("folderId");
    const q = params.get("q");
    const type = params.get("type");
    const ownerKind = params.get("ownerKind");
    if (folderId && Number.isFinite(Number(folderId))) setSelectedFolderId(Number(folderId));
    if (typeof q === "string" && q.trim()) setSearch(q.trim());
    if (typeof type === "string" && ["all", "images", "pdfs", "other"].includes(type)) setTypeFilter(type);
    if (typeof ownerKind === "string" && ["all", "valve-body", "build-sheet", "unattached"].includes(ownerKind)) {
      setOwnerFilter(ownerKind);
    }
  }, [location.search]);

  const foldersQuery = useQuery({
    queryKey: ["media-library-folders"],
    queryFn: async () => {
      const res = await api.get("/api/media-library/folders");
      return res.data || [];
    },
  });

  const filesQuery = useInfiniteQuery({
    queryKey: ["media-library-files", selectedFolderId, search, typeFilter, ownerFilter, PAGE_SIZE],
    queryFn: async ({ pageParam }) => {
      const page = typeof pageParam === "number" ? pageParam : 1;
      const params = { page, pageSize: PAGE_SIZE, q: search, type: typeFilter };
      if (ownerFilter && ownerFilter !== "all") {
        params.ownerKind = ownerFilter;
      } else {
        params.folderId = selectedFolderId;
      }
      const res = await api.get("/api/media-library/files", { params });
      // Back-compat: older versions returned a plain array.
      if (Array.isArray(res.data)) {
        return { data: res.data, pagination: { page: 1, pageSize: res.data.length, pageCount: 1, total: res.data.length } };
      }
      return res.data || { data: [], pagination: { page: 1, pageSize: PAGE_SIZE, pageCount: 1, total: 0 } };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const p = lastPage?.pagination;
      if (!p) return undefined;
      if (Number(p.page) < Number(p.pageCount)) return Number(p.page) + 1;
      return undefined;
    },
  });

  const folders = Array.isArray(foldersQuery.data) ? foldersQuery.data : [];
  const files = useMemo(() => (filesQuery.data?.pages || []).flatMap((p) => p?.data || []), [filesQuery.data]);
  const pagination = useMemo(() => {
    const pages = filesQuery.data?.pages || [];
    return pages.length ? pages[pages.length - 1]?.pagination : null;
  }, [filesQuery.data]);

  const folderTree = useMemo(() => {
    const byParent = new Map();
    folders.forEach((f) => {
      const pid = f?.parentId == null ? null : Number(f.parentId);
      const list = byParent.get(pid) || [];
      list.push(f);
      byParent.set(pid, list);
    });
    for (const [k, list] of byParent.entries()) {
      list.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
      byParent.set(k, list);
    }
    return byParent;
  }, [folders]);

  const selectedFolder = useMemo(() => folders.find((f) => Number(f.id) === Number(selectedFolderId)) || null, [folders, selectedFolderId]);
  const uploadFolder = useMemo(() => folders.find((f) => Number(f.id) === Number(uploadDialog.folderId)) || null, [folders, uploadDialog.folderId]);

  const totalCount = Number(pagination?.total || 0);

  // Auto-load next page when scrolling near the bottom (so the user doesn't have to find the button).
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return undefined;
    if (!filesQuery.hasNextPage) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (filesQuery.isFetchingNextPage) return;
        filesQuery.fetchNextPage();
      },
      { root: null, rootMargin: "600px", threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [filesQuery.hasNextPage, filesQuery.isFetchingNextPage, filesQuery.fetchNextPage]);

  const moveFileToFolder = async (fileId, folderId, opts = {}) => {
    const invalidate = opts.invalidate !== false;
    await api.post(`/api/media-library/files/${fileId}/move`, { folderId: Number(folderId) });
    if (invalidate) {
      await qc.invalidateQueries({ queryKey: ["media-library-files"] });
    }
  };

  const deleteFile = async (file) => {
    const id = Number(file?.id);
    if (!Number.isFinite(id)) return;
    const ok = window.confirm(`Delete "${file?.name || `File ${id}`}"?\n\nThis removes it from the server.`);
    if (!ok) return;
    await api.delete(`/api/upload/files/${id}`);
    await qc.invalidateQueries({ queryKey: ["media-library-files"] });
  };

  const renameFileSave = async () => {
    const id = Number(renameFile.id);
    const name = String(renameFile.name || "").trim();
    if (!Number.isFinite(id) || !name) return;
    try {
      await api.put(`/api/media-library/files/${id}`, { name });
    } catch (err) {
      await api.post(`/api/media-library/files/${id}/rename`, { name });
    }
    setRenameFile({ open: false, id: null, name: "" });
    await qc.invalidateQueries({ queryKey: ["media-library-files"] });
  };

  const createFolder = async () => {
    const name = String(newFolderName || "").trim();
    if (!name) return;
    await api.post("/api/media-library/folders", { name, parentId: selectedFolderId || null });
    setNewFolderName("");
    setCreateFolderOpen(false);
    await qc.invalidateQueries({ queryKey: ["media-library-folders"] });
  };

  const renameFolder = async () => {
    const id = Number(selectedFolderId);
    if (!Number.isFinite(id) || id === 1) return;
    const name = String(renameFolderName || "").trim();
    if (!name) return;
    await api.put(`/api/media-library/folders/${id}`, { name });
    setRenameFolderOpen(false);
    await qc.invalidateQueries({ queryKey: ["media-library-folders"] });
  };

  const deleteFolder = async () => {
    const id = Number(selectedFolderId);
    if (!Number.isFinite(id) || id === 1) {
      window.alert('The root "API Uploads" folder cannot be deleted.');
      return;
    }
    const ok = window.confirm(`Delete folder "${selectedFolder?.name || `Folder ${id}`}"?\n\nFolder must be empty (no files, no subfolders).`);
    if (!ok) return;
    try {
      await api.post(`/api/media-library/folders/${id}/delete`);
      setSelectedFolderId(1);
      await qc.invalidateQueries({ queryKey: ["media-library-folders"] });
      await qc.invalidateQueries({ queryKey: ["media-library-files", id] });
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not delete folder.";
      window.alert(msg);
    }
  };

  const onUpload = async (filesToUpload, folderId) => {
    if (!filesToUpload?.length) return;
    const targetFolderId = folderId ? Number(folderId) : 1;
    try {
      setUploadStatus({ uploading: true, stage: "uploading", total: filesToUpload.length, moved: 0, uploaded: 0, error: "" });
      // Upload in ONE request (more reliable on mobile + supports multi-select).
      const formData = new FormData();
      filesToUpload.forEach((file) => formData.append("files", file));
      formData.append("ref", "api::build-sheet.build-sheet");

      // Important: don't manually set Content-Type; let the browser set the multipart boundary.
      const res = await api.post("/api/upload", formData);
      const uploaded = Array.isArray(res?.data) ? res.data : [];
      setUploadStatus((p) => ({ ...p, stage: "moving", uploaded: uploaded.length }));

      if (targetFolderId && Number(targetFolderId) !== 1) {
        // Move sequentially with retries: sometimes the upload record isn't immediately movable.
        const failures = [];
        let moved = 0;
        for (const f of uploaded) {
          const fileId = Number(f?.id);
          if (!Number.isFinite(fileId)) continue;
          let ok = false;
          let lastErr = null;
          for (let attempt = 0; attempt < 6 && !ok; attempt += 1) {
            try {
              await moveFileToFolder(fileId, targetFolderId, { invalidate: false });
              ok = true;
            } catch (err) {
              lastErr = err;
              await new Promise((r) => setTimeout(r, 150 * (attempt + 1)));
            }
          }
          if (!ok) failures.push({ fileId, err: lastErr });
          else moved += 1;
          setUploadStatus((p) => ({ ...p, moved }));
        }
        if (failures.length) {
          console.warn("[gallery-mobile] Some uploads could not be moved to folder", {
            targetFolderId,
            failures: failures.map((x) => x.fileId),
          });
        }
      }
      await qc.invalidateQueries({ queryKey: ["media-library-files"] });
      setUploadStatus((p) => ({ ...p, uploading: false, stage: "done" }));
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Upload failed.";
      setUploadStatus((p) => ({ ...p, uploading: false, stage: "error", error: msg }));
      window.alert(msg);
    }
  };

  const renderFolderNode = (node, depth) => {
    const kids = folderTree.get(Number(node.id)) || [];
    return (
      <Box key={node.id}>
        <ListItemButton
          selected={Number(selectedFolderId) === Number(node.id)}
          onClick={() => {
            setSelectedFolderId(Number(node.id));
            setFolderPickerOpen(false);
          }}
          sx={{ borderRadius: 1, pl: 1 + depth * 2 }}
        >
          <Icon fontSize="small" sx={{ mr: 1, color: Number(selectedFolderId) === Number(node.id) ? "info.main" : "text.secondary" }}>
            folder
          </Icon>
          <ListItemText
            primary={node.name || `Folder ${node.id}`}
            primaryTypographyProps={{ fontWeight: 900, noWrap: true }}
            secondary={node.path || ""}
            secondaryTypographyProps={{ variant: "caption" }}
          />
        </ListItemButton>
        {kids.map((k) => renderFolderNode(k, depth + 1))}
      </Box>
    );
  };

  const renderUploadTargetNode = (node, depth) => {
    const kids = folderTree.get(Number(node.id)) || [];
    return (
      <Box key={node.id}>
        <ListItemButton
          selected={Number(uploadDialog.folderId) === Number(node.id)}
          onClick={() => setUploadDialog((p) => ({ ...p, folderId: Number(node.id) }))}
          sx={{ borderRadius: 1, pl: 1 + depth * 2 }}
        >
          <Icon fontSize="small" sx={{ mr: 1, color: Number(uploadDialog.folderId) === Number(node.id) ? "info.main" : "text.secondary" }}>
            drive_folder_upload
          </Icon>
          <ListItemText
            primary={node.name || `Folder ${node.id}`}
            primaryTypographyProps={{ fontWeight: 900, noWrap: true }}
            secondary={node.path || ""}
            secondaryTypographyProps={{ variant: "caption" }}
          />
        </ListItemButton>
        {kids.map((k) => renderUploadTargetNode(k, depth + 1))}
      </Box>
    );
  };

  return (
    <MobilePage
      title="Gallery"
      subtitle={
        filesQuery.isFetching
          ? `${selectedFolder?.name || "Folder"} • Syncing…`
          : `${selectedFolder?.name || "Folder"} • ${files.length}${totalCount ? ` of ${totalCount}` : ""} item${files.length === 1 ? "" : "s"}`
      }
      right={
        <Stack direction="row" spacing={0.75} alignItems="center">
          {filesQuery.isFetching ? <Chip size="small" label="Syncing…" variant="outlined" /> : null}
          <IconButton
            aria-label="Upload"
            size="small"
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
            onClick={() => setUploadDialog({ open: true, folderId: selectedFolderId || 1 })}
          >
            <Icon fontSize="small">cloud_upload</Icon>
          </IconButton>
        </Stack>
      }
      onBack={() => navigate("/m/dashboard")}
    >
      <Box sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "background.default", pb: 1 }}>
        <TextField
          fullWidth
          placeholder="Search tag, build sheet, or filename…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Stack direction="row" spacing={0.75} sx={{ pt: 1, overflowX: "auto" }}>
          {OWNER_FILTERS.map((filter) => (
            <Chip
              key={filter.value}
              size="small"
              clickable
              color={ownerFilter === filter.value ? "primary" : "default"}
              variant={ownerFilter === filter.value ? "filled" : "outlined"}
              label={filter.label}
              onClick={() => setOwnerFilter(filter.value)}
              sx={{ flex: "0 0 auto" }}
            />
          ))}
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ pt: 1, overflowX: "auto" }}>
          {ownerFilter === "all" ? (
            <>
              <Chip
                size="small"
                clickable
                variant="outlined"
                label={selectedFolder?.name || "Choose folder"}
                onClick={() => setFolderPickerOpen(true)}
                icon={<Icon sx={{ fontSize: 16 }}>folder_open</Icon>}
                sx={{ flex: "0 0 auto" }}
              />
              <Chip
                size="small"
                clickable
                variant="outlined"
                label="New folder"
                onClick={() => setCreateFolderOpen(true)}
                icon={<Icon sx={{ fontSize: 16 }}>create_new_folder</Icon>}
                sx={{ flex: "0 0 auto" }}
              />
              <Box sx={{ width: 6, flex: "0 0 auto" }} />
            </>
          ) : null}
          <Chip
            size="small"
            clickable
            variant={typeFilter === "all" ? "filled" : "outlined"}
            label="All"
            onClick={() => setTypeFilter("all")}
            sx={{ flex: "0 0 auto" }}
          />
          <Chip
            size="small"
            clickable
            variant={typeFilter === "images" ? "filled" : "outlined"}
            label="Images"
            onClick={() => setTypeFilter("images")}
            sx={{ flex: "0 0 auto" }}
          />
          <Chip
            size="small"
            clickable
            variant={typeFilter === "pdfs" ? "filled" : "outlined"}
            label="PDFs"
            onClick={() => setTypeFilter("pdfs")}
            sx={{ flex: "0 0 auto" }}
          />
          <Chip
            size="small"
            clickable
            variant={typeFilter === "other" ? "filled" : "outlined"}
            label="Other"
            onClick={() => setTypeFilter("other")}
            sx={{ flex: "0 0 auto" }}
          />
          {ownerFilter === "all" ? (
            <>
              <Box sx={{ width: 6, flex: "0 0 auto" }} />
              <Chip
                size="small"
                clickable
                variant="outlined"
                label="Rename"
                disabled={Number(selectedFolderId) === 1}
                onClick={() => {
                  setRenameFolderName(selectedFolder?.name || "");
                  setRenameFolderOpen(true);
                }}
                sx={{ flex: "0 0 auto" }}
              />
              <Chip
                size="small"
                clickable
                variant="outlined"
                label="Delete folder"
                disabled={Number(selectedFolderId) === 1}
                onClick={deleteFolder}
                sx={{ flex: "0 0 auto" }}
              />
            </>
          ) : null}
        </Stack>
      </Box>

      {filesQuery.isLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}

      <Stack spacing={0.75}>
        {filesQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {files.map((file) => {
              const url = getFileUrl(file);
              const isImage = String(file?.mime || "").startsWith("image/");
              const isPdf = String(file?.mime || "") === "application/pdf" || String(file?.name || "").toLowerCase().endsWith(".pdf");
              const title = ownershipTitle(file);
              const subtitle = ownershipSubtitle(file);
              return (
                <Card key={file.id} variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
                  <CardActionArea
                    onClick={() => {
                      if (!url) return;
                      if (!isImage && !isPdf) return;
                      setViewer({ open: true, file });
                    }}
                    sx={{ p: 1 }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar
                          src={isImage ? url || undefined : undefined}
                          variant="rounded"
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 1,
                            bgcolor: "grey.100",
                            "& img": { objectFit: "cover" },
                          }}
                        >
                          {!isImage ? <Icon fontSize="small">{isPdf ? "picture_as_pdf" : "description"}</Icon> : null}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={900} noWrap>
                            {title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {subtitle}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ opacity: 0.75, display: "block" }}>
                            {file?.name || `File ${file?.id}`}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <IconButton
                          size="small"
                          aria-label="Rename file"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setRenameFile({ open: true, id: file.id, name: file?.name || "" });
                          }}
                        >
                          <Icon fontSize="small">edit</Icon>
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Move file"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setMoveDialog({ open: true, file, targetFolderId: selectedFolderId });
                          }}
                        >
                          <Icon fontSize="small">drive_file_move</Icon>
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Delete file"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            deleteFile(file);
                          }}
                        >
                          <Icon fontSize="small" color="error">
                            delete
                          </Icon>
                        </IconButton>
                        <Icon fontSize="small">chevron_right</Icon>
                      </Stack>
                    </Stack>
                  </CardActionArea>
                </Card>
              );
            })}

            {filesQuery.hasNextPage ? (
              <Box sx={{ pt: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="info"
                  onClick={() => filesQuery.fetchNextPage()}
                  disabled={filesQuery.isFetchingNextPage}
                  startIcon={<Icon fontSize="small">expand_more</Icon>}
                  sx={{ py: 1.25, borderRadius: 1 }}
                >
                  {filesQuery.isFetchingNextPage ? "Loading more…" : "Load more"}
                </Button>
              </Box>
            ) : null}

            {/* Sentinel for auto-pagination */}
            <Box ref={loadMoreRef} sx={{ height: 1 }} />

            {!files.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, py: 2 }}>
                No files found.
              </Typography>
            ) : null}
          </>
        )}
      </Stack>

      {/* Folder picker */}
      <Dialog open={folderPickerOpen} onClose={() => setFolderPickerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Choose folder
          <IconButton onClick={() => setFolderPickerOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {foldersQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <List dense>
              {(folderTree.get(null) || []).map((root) => renderFolderNode(root, 0))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderPickerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Upload dialog (pick folder + pick files) */}
      <Dialog open={uploadDialog.open} onClose={() => setUploadDialog({ open: false, folderId: selectedFolderId || 1 })} fullWidth maxWidth="sm">
        <DialogTitle>
          Upload files
          <IconButton onClick={() => setUploadDialog({ open: false, folderId: selectedFolderId || 1 })} sx={{ position: "absolute", right: 8, top: 8 }}>
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {uploadStatus.uploading ? (
            <Box sx={{ mb: 1.25 }}>
              <LinearProgress />
              <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between" sx={{ mt: 0.75 }}>
                <Typography variant="body2" fontWeight={900}>
                  {uploadStatus.stage === "uploading"
                    ? `Uploading ${uploadStatus.total} file${uploadStatus.total === 1 ? "" : "s"}…`
                    : `Moving ${uploadStatus.moved}/${uploadStatus.uploaded || uploadStatus.total}…`}
                </Typography>
                <Chip
                  size="small"
                  color="info"
                  label={
                    uploadStatus.stage === "uploading"
                      ? `Uploading (${uploadStatus.total})`
                      : `Moved ${uploadStatus.moved}/${uploadStatus.uploaded || uploadStatus.total}`
                  }
                />
              </Stack>
            </Box>
          ) : uploadStatus.stage === "done" ? (
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Typography variant="body2" fontWeight={900}>
                Upload complete.
              </Typography>
              <Chip size="small" color="success" label={`Uploaded ${uploadStatus.uploaded || 0}`} />
            </Stack>
          ) : uploadStatus.stage === "error" ? (
            <Box sx={{ mb: 1.25 }}>
              <Typography variant="body2" fontWeight={900} color="error">
                Upload failed.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {uploadStatus.error || ""}
              </Typography>
            </Box>
          ) : null}

          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={900}>
              Destination
            </Typography>
            <Chip size="small" label={uploadFolder?.name || selectedFolder?.name || "API Uploads"} />
          </Stack>
          <Divider sx={{ mb: 1 }} />
          {foldersQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <List dense>
              {(folderTree.get(null) || []).map((root) => renderUploadTargetNode(root, 0))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="info"
            disabled={uploadStatus.uploading}
            onClick={() => setUploadDialog((p) => ({ ...p, folderId: selectedFolderId || 1 }))}
          >
            Use current folder
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            color="info"
            component="label"
            disabled={uploadStatus.uploading}
            startIcon={<Icon fontSize="small">cloud_upload</Icon>}
          >
            {uploadStatus.uploading ? "Uploading…" : "Choose files & upload"}
            <input
              type="file"
              hidden
              multiple
              accept="image/*,application/pdf"
              onChange={async (e) => {
                const picked = Array.from(e.target.files || []);
                try {
                  await onUpload(picked, uploadDialog.folderId);
                } finally {
                  e.target.value = "";
                }
              }}
            />
          </Button>
          {uploadStatus.stage === "done" ? (
            <Button
              variant="outlined"
              color="success"
              onClick={() => {
                setUploadDialog({ open: false, folderId: selectedFolderId || 1 });
                setUploadStatus({ uploading: false, stage: "idle", total: 0, moved: 0, uploaded: 0, error: "" });
              }}
            >
              Done
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      {/* Create folder */}
      <Dialog open={createFolderOpen} onClose={() => setCreateFolderOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Create folder
          <IconButton onClick={() => setCreateFolderOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth label="Folder name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Parent: {selectedFolder?.name || "Top level"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderOpen(false)}>Cancel</Button>
          <Button variant="contained" color="info" onClick={createFolder} disabled={!String(newFolderName || "").trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename folder */}
      <Dialog open={renameFolderOpen} onClose={() => setRenameFolderOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Rename folder
          <IconButton onClick={() => setRenameFolderOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth label="Folder name" value={renameFolderName} onChange={(e) => setRenameFolderName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameFolderOpen(false)}>Cancel</Button>
          <Button variant="contained" color="info" onClick={renameFolder} disabled={!String(renameFolderName || "").trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move file */}
      <Dialog
        open={moveDialog.open}
        onClose={() => setMoveDialog({ open: false, file: null, targetFolderId: selectedFolderId })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Move file
          <IconButton onClick={() => setMoveDialog({ open: false, file: null, targetFolderId: selectedFolderId })} sx={{ position: "absolute", right: 8, top: 8 }}>
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" fontWeight={800} sx={{ mb: 1 }}>
            {moveDialog.file?.name || "File"}
          </Typography>
          <List dense>
            {(folderTree.get(null) || []).map((root) => {
              const renderPick = (node, depth) => {
                const kids = folderTree.get(Number(node.id)) || [];
                return (
                  <Box key={node.id}>
                    <ListItemButton
                      selected={Number(moveDialog.targetFolderId) === Number(node.id)}
                      onClick={() => setMoveDialog((p) => ({ ...p, targetFolderId: Number(node.id) }))}
                      sx={{ borderRadius: 1, pl: 1 + depth * 2 }}
                    >
                      <ListItemText primary={node.name || `Folder ${node.id}`} primaryTypographyProps={{ fontWeight: 800, noWrap: true }} />
                    </ListItemButton>
                    {kids.map((k) => renderPick(k, depth + 1))}
                  </Box>
                );
              };
              return renderPick(root, 0);
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialog({ open: false, file: null, targetFolderId: selectedFolderId })}>Cancel</Button>
          <Button
            variant="contained"
            color="info"
            onClick={async () => {
              const fileId = Number(moveDialog.file?.id);
              const folderId = Number(moveDialog.targetFolderId);
              if (!Number.isFinite(fileId) || !Number.isFinite(folderId)) return;
              await moveFileToFolder(fileId, folderId);
              setMoveDialog({ open: false, file: null, targetFolderId: selectedFolderId });
            }}
          >
            Move
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename file */}
      <Dialog open={renameFile.open} onClose={() => setRenameFile({ open: false, id: null, name: "" })} fullWidth maxWidth="sm">
        <DialogTitle>
          Rename file
          <IconButton onClick={() => setRenameFile({ open: false, id: null, name: "" })} sx={{ position: "absolute", right: 8, top: 8 }}>
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth label="File name" value={renameFile.name} onChange={(e) => setRenameFile((p) => ({ ...p, name: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameFile({ open: false, id: null, name: "" })}>Cancel</Button>
          <Button variant="contained" color="info" onClick={renameFileSave} disabled={!String(renameFile.name || "").trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Viewer */}
      <MobileImageViewer
        open={viewer.open}
        file={viewer.file}
        baseURL={api?.defaults?.baseURL || ""}
        onClose={() => setViewer({ open: false, file: null })}
      />
    </MobilePage>
  );
}
